import type {
  Notification,
  NotificationListParams,
  NotificationUnreadCount,
} from "@facade/contracts";
import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { fetchAuthSession } from "aws-amplify/auth";

import { AppConfig } from "@facade/config";
import { notificationQueryKeys } from "../queries";
import { projectQueryKeys } from "@facade/features/projects/queries";
import {
  adjustProjectUnreadNotificationCount,
  notificationMatchesList,
  type NotificationListCache,
} from "../mutations/notificationCache";

type NotificationCreatedMessage = {
  type: "notification.created";
  notification: Notification;
};

export function useNotificationRealtime() {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!AppConfig.websocketUrl) return;

    let socket: WebSocket | null = null;
    let reconnectTimer: number | undefined;
    let reconnectAttempt = 0;
    let cancelled = false;
    const receivedNotificationIds = new Set<string>();

    const reconcileCounts = () => {
      void queryClient.invalidateQueries({
        queryKey: notificationQueryKeys.all,
      });
      void queryClient.invalidateQueries({
        queryKey: projectQueryKeys.all,
      });
    };

    const rememberNotification = (notificationId: string) => {
      if (receivedNotificationIds.has(notificationId)) return false;

      if (receivedNotificationIds.size >= 500) {
        const oldestNotificationId = receivedNotificationIds.values().next()
          .value;
        if (oldestNotificationId) {
          receivedNotificationIds.delete(oldestNotificationId);
        }
      }

      receivedNotificationIds.add(notificationId);
      return true;
    };

    const handleNotificationCreated = async (notification: Notification) => {
      if (!rememberNotification(notification.id)) return;

      await Promise.all([
        queryClient.cancelQueries({
          queryKey: notificationQueryKeys.unreadCount(),
          exact: true,
        }),
        queryClient.cancelQueries({
          queryKey: projectQueryKeys.all,
          exact: true,
        }),
      ]);

      queryClient
        .getQueriesData<NotificationListCache>({
          queryKey: notificationQueryKeys.lists(),
        })
        .forEach(([queryKey, current]) => {
          if (!current) return;
          const params = queryKey[2] as
            | Omit<NotificationListParams, "cursor">
            | undefined;
          if (!params || !notificationMatchesList(notification, params)) {
            return;
          }
          const exists = current.pages.some((page) =>
            page.notifications.some(
              (currentNotification) =>
                currentNotification.id === notification.id,
            ),
          );
          if (exists) return;

          queryClient.setQueryData<NotificationListCache>(queryKey, {
            ...current,
            pages: current.pages.map((page, index) =>
              index === 0
                ? {
                    ...page,
                    notifications: [notification, ...page.notifications],
                  }
                : page,
            ),
          });
        });

      if (!notification.readAt) {
        queryClient.setQueryData<NotificationUnreadCount>(
          notificationQueryKeys.unreadCount(),
          (current) => ({ count: (current?.count ?? 0) + 1 }),
        );
        if (notification.projectId) {
          adjustProjectUnreadNotificationCount(
            queryClient,
            notification.projectId,
            1,
          );
        }
      }
    };

    const handleWindowFocus = () => reconcileCounts();
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") reconcileCounts();
    };

    window.addEventListener("focus", handleWindowFocus);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    const connect = async () => {
      try {
        const session = await fetchAuthSession();
        const token = session.tokens?.accessToken?.toString();
        if (!token || cancelled) return;

        const url = new URL(AppConfig.websocketUrl);
        url.searchParams.set("token", token);
        socket = new WebSocket(url);

        socket.onopen = () => {
          reconnectAttempt = 0;
          reconcileCounts();
        };

        socket.onmessage = (event) => {
          const message = JSON.parse(event.data) as NotificationCreatedMessage;
          if (message.type !== "notification.created") return;
          void handleNotificationCreated(message.notification);
        };

        socket.onclose = () => {
          if (cancelled) return;
          const delay = Math.min(30_000, 1_000 * 2 ** reconnectAttempt);
          reconnectAttempt += 1;
          reconnectTimer = window.setTimeout(() => void connect(), delay);
        };
      } catch {
        if (cancelled) return;
        const delay = Math.min(30_000, 1_000 * 2 ** reconnectAttempt);
        reconnectAttempt += 1;
        reconnectTimer = window.setTimeout(() => void connect(), delay);
      }
    };

    void connect();

    return () => {
      cancelled = true;
      if (reconnectTimer !== undefined) window.clearTimeout(reconnectTimer);
      window.removeEventListener("focus", handleWindowFocus);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      socket?.close();
    };
  }, [queryClient]);
}
